import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { evaluateFormula } from "../utils/formulas";
import Field from "./Field";

const SAFE_EXPR = /^[\p{L}\p{N}_\s"'.<>=!&|()\-]+$/u;
const numberLike = (s) =>
  typeof s === "string" && /^-?\d+(\.\d+)?$/.test(s.trim());
const normalizeValues = (values) => {
  const out = {};
  for (const k of Object.keys(values || {})) {
    const v = values[k];
    if (v === "" || v == null) out[k] = "";
    else if (Array.isArray(v)) out[k] = v.length;
    else if (numberLike(v)) out[k] = Number(v);
    else out[k] = v;
  }
  return out;
};

export function evalVisibleIf(expr, values) {
  if (!expr || !String(expr).trim()) return true;
  // Masquer le contenu entre guillemets pour le contrôle de sécurité
  const masked = String(expr).replace(/(['"])(?:\\.|(?!\1).)*\1/g, '""');
  if (!SAFE_EXPR.test(masked)) return true;
  const v = normalizeValues(values);
  const keys = Object.keys(v).sort((a, b) => b.length - a.length);
  let js = String(expr);
  keys.forEach((k) => {
    const re = new RegExp(
      `\\b${k.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`,
      "g"
    );
    js = js.replace(re, `v[${JSON.stringify(k)}]`);
  });
  try {
    return !!new Function("v", `return !!(${js});`)(v);
  } catch {
    return true;
  }
}

export default function DynamicForm({
  fields = [],
  sections = [],
  values,
  setValues,
  showPlaceholders = false,
  onFieldFocus,
  onFieldChangeKey,
  showSectionNav = false,
}) {
  // valeurs par défaut
  useEffect(() => {
    setValues((prev) => {
      const next = { ...prev };
      fields.forEach((f) => {
        if (next[f.key] === undefined) {
          if (f.type === "checkbox") {
            if (f.default) {
              next[f.key] = f.template_if_checked || "Oui";
            } else {
              next[f.key] = f.template_if_unchecked || "";
            }
          } else {
            if ((next[f.key] === "" || next[f.key] === undefined) && f.use_map && f.mapDefaultValue != null) {
              next[f.key] = f.mapDefaultValue;
            } else {
              next[f.key] = f.default ?? "";
            }
          }
        }
      });
      return next;
    });
  }, [fields, setValues]);

  // [ok] Recalcul des formules (y compris dans repeat_section)
  useEffect(() => {
    let changed = false;
    const next = { ...values };

    fields.forEach((f) => {
      if (f.formula) {
        // Cas normal (non répétitif)
        if (!f.group_key || !f.repeat_section) {
          const result = evaluateFormula(f.formula, values);
          if (next[f.key] !== result) {
            next[f.key] = result;
            changed = true;
          }
        } 
        // Cas répétitif -> appliquer la formule dans chaque ligne
        else {
          const groupName = f.group_key;
          if (Array.isArray(next[groupName])) {
            next[groupName] = next[groupName].map((row, idx) => {
              const rowValues = { ...row };
              const result = evaluateFormula(f.formula, rowValues);
              if (rowValues[f.key] !== result) {
                rowValues[f.key] = result;
                changed = true;
              }
              return rowValues;
            });
          }
        }
      }
    });

    if (changed) setValues(next);
  }, [values, fields, setValues]);

  const byKey = useMemo(() => {
    const m = new Map();
    fields.forEach((f) => m.set(f.key, f));
    return m;
  }, [fields]);

  const grouped = useMemo(() => {
    if (!sections || sections.length === 0)
      return [
        {
          id: "__all",
          label: "Tous les champs",
          fields: fields.map((f) => f.key),
        },
      ];
    const listed = new Set();
    const groups = sections.map((s) => ({
      ...s,
      fields: (s.fields || []).filter((k) => byKey.has(k)),
    }));
    groups.forEach((g) => g.fields.forEach((k) => listed.add(k)));
    const leftovers = fields
      .map((f) => f.key)
      .filter((k) => !listed.has(k));
    if (leftovers.length)
      groups.push({ id: "__autres", label: "Autres", fields: leftovers });
    return groups;
  }, [sections, fields, byKey]);

  const [active, setActive] = useState(grouped[0]?.id || null);

  const activeIndex = useMemo(() => {
    const idx = grouped.findIndex((g) => g.id === active);
    return idx >= 0 ? idx : 0;
  }, [grouped, active]);

  const goPrevSection = useCallback(() => {
    if (!grouped.length) return;
    const idx = Math.max(0, activeIndex - 1);
    setActive(grouped[idx].id);
  }, [grouped, activeIndex]);

  const goNextSection = useCallback(() => {
    if (!grouped.length) return;
    const idx = Math.min(grouped.length - 1, activeIndex + 1);
    setActive(grouped[idx].id);
  }, [grouped, activeIndex]);
  useEffect(() => {
    if (!grouped.find((g) => g.id === active))
      setActive(grouped[0]?.id || null);
  }, [grouped, active]);

  // refs
  const inputRefs = useRef(new Map());
  const registerRef = useCallback((key, el) => {
    if (el) inputRefs.current.set(key, el);
  }, []);

  // [ok] update avec gestion template_if_checked / unchecked
  const update = useCallback(
    (key, val) => {
      const meta = byKey.get(key);
      if (meta?.readOnly || meta?.formula) return;

      let finalVal = val;

      if (meta?.type === "checkbox") {
        if (val) {
          finalVal = meta.template_if_checked || "Oui";
        } else {
          finalVal = meta.template_if_unchecked ?? "";
        }
      }

      setValues((v) => {
        if (v[key] === finalVal) return v;
        return { ...v, [key]: finalVal };
      });
    },
    [byKey, setValues]
  );

  const asInputValue = (v) =>
    v === true || v === false ? "" : v ?? "";

  // [ok] Nouveau : regroupement par group_key
  const groupByGroupKey = (fieldKeys) => {
    const groupedMap = {};
    fieldKeys.forEach((k) => {
      const f = byKey.get(k);
      if (!f) return;
      const gk = f.group_key || "";
      if (!groupedMap[gk]) groupedMap[gk] = [];
      groupedMap[gk].push(f);
    });
    return groupedMap;
  };

  // [ok] Gestion des lignes répétables
  const handleAddRow = (groupKey) => {
    setValues((prev) => {
      const current = prev[groupKey] || [];
      return { ...prev, [groupKey]: [...current, {}] };
    });
  };

  const handleRemoveRow = (groupKey, idx) => {
    setValues((prev) => {
      const current = prev[groupKey] || [];
      const updated = current.filter((_, i) => i !== idx);
      return { ...prev, [groupKey]: updated };
    });
  };     
  return (
    <div>
      {grouped.length > 1 && (
        <div
          className="tabs"
          style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}
        >
          {grouped.map((g) => (
            <button
              key={g.id}
              className={`btn ${g.id === active ? "btn-primary" : "btn-outline"}`}
              onClick={() => setActive(g.id)}
            >
              {g.label}
            </button>
          ))}
        </div>
      )}

      {grouped.map(
        (g) =>
          g.id === active && (
            <div key={g.id} className="form-section">
              {Object.entries(groupByGroupKey(g.fields)).map(([gk, fieldsInGroup]) => {
                const isRepeatable = fieldsInGroup.some(
                  (f) => String(f.repeat_section).toLowerCase() === "true"
                );                
                if (isRepeatable && gk) {
                  const items = values[gk] || [{}];
                  return (
                    <div key={gk} className="repeat-section">
                      <div className="group-title">{gk}</div>
                      {items.map((item, idx) => (
                        <div key={`${gk}-${idx}`} className="repeat-block">
                          <div className="form-grid">
                            {fieldsInGroup.map((f) => {
                              let val = item[f.key] ?? "";
                              // [ok] Évaluer la formule si définie
                              if (f.formula) {
                                try {
                                  //console.log("DEBUG DynamicForm -> formule:", f.formula, "item:", item);
                                  val = evaluateFormula(f.formula, item); // calcul sur la ligne
                                  //console.log("DEBUG DynamicForm -> résultat formule:", f.formula, "=", val);
                                } catch (e) {
                                  //console.warn("Erreur formule (repeat-block):", f.formula, e);
                                  val = "erreur formule";
                                }
                              }

                              // Contexte de visibilité : ligne + global
                              const ctx = { ...values, ...item };
                              if (!evalVisibleIf(f.visibleIf || f.visible_if, ctx)) return null;
                              return (
                                <Field
                                  key={f.key}
                                  f={f}
                                  raw={val}
                                  inDocx={f.inDocx}
                                  val={val}
                                  isRO={Boolean(f.readOnly || f.formula)}
                                  showPlaceholders={showPlaceholders}
                                  onFieldFocus={onFieldFocus}
                                  onFieldChangeKey={onFieldChangeKey}
                                  registerRef={registerRef}
                                  update={(key, newVal) => {
                                    // [ok] Mettre à jour uniquement cette ligne
                                    setValues((prev) => {
                                      const updated = [...(prev[gk] || [])];
                                      updated[idx] = { ...updated[idx], [key]: newVal };
                                      return { ...prev, [gk]: updated };
                                    });
                                  }}
                                />
                              );
                            })}
                          </div>

                          <button
                            type="button"
                            className="icon-btn danger"
                            onClick={() => handleRemoveRow(gk, idx)}
                            title="Supprimer"
                          >
                            [trash]
                          </button>
                        </div>

                      ))}
                      <button
                        type="button"
                        className="icon-btn"
                        onClick={() => handleAddRow(gk)}
                        title="Ajouter"
                      >
                        +
                      </button>
                    </div>
                  );
                }

                // Groupe normal -> rendu vertical
                return (
                  <div key={gk} className="form-group-block">
                    {gk && <div className="group-title">{gk}</div>}
                    <div className="form-grid">
                      {fieldsInGroup.map((f) => {
                        if (!evalVisibleIf(f.visibleIf || f.visible_if, values))
                          return null;
                        return (
                          <Field
                            key={f.key}
                            f={f}                            
                            raw={values[f.key]}
                            inDocx={f.inDocx}
                            val={asInputValue(values[f.key])}
                            isRO={Boolean(f.readOnly || f.formula)}
                            showPlaceholders={showPlaceholders}
                            onFieldFocus={onFieldFocus}
                            onFieldChangeKey={onFieldChangeKey}
                            registerRef={registerRef}
                            update={update}
                          />
                        );
                      })}
                    </div>
                  </div>
                );
              })}


              {showSectionNav && grouped.length > 1 && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 12,
                    marginTop: 16,
                  }}
                >
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={goPrevSection}
                    disabled={activeIndex <= 0}
                  >
                    Précédent
                  </button>

                  <div style={{ fontSize: 13, opacity: 0.8 }}>
                    Section {activeIndex + 1} / {grouped.length}
                  </div>

                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={goNextSection}
                    disabled={activeIndex >= grouped.length - 1}
                  >
                    Suivant
                  </button>
                </div>
              )}
            </div>
          )
      )}
    </div>
  );
}
